import React, { useEffect, useMemo, useState } from 'react'
import axiosInstance from '../../api/axiosInstance'
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  MinusCircleIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import type { NotaCreditoLinea } from '../../services/orders/ofimaRemisionFacturaService'
import Switch from '../ui/Input/Switch'

export type CancelStepStatus = 'pending' | 'in_progress' | 'done' | 'error' | 'skipped'

export interface CancelStep {
  key: string
  label: string
  status: CancelStepStatus
  message?: string
}

export interface CancelOrderConfirmPayload {
  ncParcial: boolean
  ncPartialLineas?: NotaCreditoLinea[]
  ncPartialSkus?: string[]
}

interface CancelOrderModalProps {
  isOpen: boolean
  onClose: () => void
  order: {
    id: number
    orders_id: string
    customer_full_name?: string
    first_name?: string
    last_name?: string
    store_name: string
    total: string
    id_remision?: string
  } | null
  onConfirm: (payload: CancelOrderConfirmPayload) => void
  isLoading?: boolean
  cancelSteps?: CancelStep[]
}

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  isOpen,
  onClose,
  order,
  onConfirm,
  isLoading = false,
  cancelSteps = []
}) => {
  const idRemisionStr = order?.id_remision ? String(order.id_remision).trim() : ''
  const hasRemision = idRemisionStr !== ''
  const displayRemision = hasRemision ? idRemisionStr.replace(/\.+$/, '') : '-'
  const hasSteps = cancelSteps.length > 0
  const anyStepInProgress = cancelSteps.some(s => s.status === 'in_progress')
  const showNoRemisionInfo = !hasRemision
  const orderId = order?.id

  const [ncParcial, setNcParcial] = useState(false)
  const [ncProductosLoading, setNcProductosLoading] = useState(false)
  const [ncProductosError, setNcProductosError] = useState<string | null>(null)
  const [ncProductoOptions, setNcProductoOptions] = useState<
    Array<{
      producto: string
      productName?: string
      cantidad: number
      valorUnitario: number
    }>
  >([])
  const [selectedNcSkus, setSelectedNcSkus] = useState<string[]>([])

  useEffect(() => {
    if (!isOpen || !orderId) return
    setNcParcial(false)
    setNcProductosLoading(false)
    setNcProductosError(null)
    setNcProductoOptions([])
    setSelectedNcSkus([])
  }, [isOpen, orderId])

  useEffect(() => {
    if (!isOpen || !orderId || !ncParcial) return

    let cancelled = false

    const loadProducts = async () => {
      try {
        setNcProductosLoading(true)
        setNcProductosError(null)

        const response = await axiosInstance.get(`/list/${orderId}`)
        const lineItems = response?.data?.lineItems
        const items: Array<{
          product_id: string
          product_name?: string
          quantity: number
          price: string | number
        }> = Array.isArray(lineItems) ? lineItems : []

        // Agrupar por SKU para construir lineas de NC parcial
        const bySku = new Map<
          string,
          { producto: string; productName?: string; cantidad: number; valorUnitario: number }
        >()

        for (const item of items) {
          const sku = String(item.product_id ?? '').trim()
          if (!sku) continue

          const qty = Number(item.quantity)
          const unitPrice = Number(item.price)

          if (!Number.isFinite(qty) || qty <= 0) continue
          if (!Number.isFinite(unitPrice) || unitPrice <= 0) continue

          const existing = bySku.get(sku)
          if (existing) {
            existing.cantidad += qty
          } else {
            bySku.set(sku, {
              producto: sku,
              productName: item.product_name ? String(item.product_name) : undefined,
              cantidad: qty,
              valorUnitario: unitPrice
            })
          }
        }

        if (cancelled) return
        setNcProductoOptions(Array.from(bySku.values()))
        setSelectedNcSkus([])
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : 'Error al cargar productos'
        setNcProductosError(msg)
      } finally {
        if (cancelled) return
        setNcProductosLoading(false)
      }
    }

    loadProducts()

    return () => {
      cancelled = true
    }
  }, [isOpen, orderId, ncParcial])

  const selectedNcLineas = useMemo<NotaCreditoLinea[]>(() => {
    if (!ncParcial) return []
    const selectedSet = new Set(selectedNcSkus)
    return ncProductoOptions
      .filter(o => selectedSet.has(o.producto))
      .map(o => ({
        producto: o.producto,
        cantidad: o.cantidad,
        valorUnitario: o.valorUnitario
      }))
  }, [ncParcial, ncProductoOptions, selectedNcSkus])

  const canConfirm = !anyStepInProgress && (!ncParcial || selectedNcSkus.length > 0)

  const handleConfirm = () => {
    if (ncParcial) {
      onConfirm({
        ncParcial: true,
        ncPartialLineas: selectedNcLineas,
        ncPartialSkus: [...selectedNcSkus]
      })
      return
    }

    onConfirm({ ncParcial: false })
  }

  if (!isOpen || !order) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Cancelar Orden
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              ¿Estás seguro de que deseas cancelar esta orden?
            </p>

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    ID de Orden
                  </p>
                  <p className="text-sm font-semibold text-gray-900 truncate" title={order.orders_id}>
                    {order.orders_id}
                  </p>
                </div>

                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Cliente
                  </p>
                  <p
                    className="text-sm font-semibold text-gray-900 truncate"
                    title={order.customer_full_name || `${order.first_name || ''} ${order.last_name || ''}`.trim() || 'Sin nombre'}
                  >
                    {order.customer_full_name || `${order.first_name || ''} ${order.last_name || ''}`.trim() || 'Sin nombre'}
                  </p>
                </div>

                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Remisión
                  </p>
                  <p className="text-sm font-semibold text-gray-900 truncate" title={displayRemision}>
                    {displayRemision}
                  </p>
                </div>
              </div>
            </div>

            {showNoRemisionInfo && (
              <p className="mt-4 text-sm text-blue-700 bg-blue-50 rounded-lg p-3">
                Este pedido no tiene remisión en SGL. Se consultará si existe en OFIMA; si hay remisión se facturará y creará nota crédito antes de cancelar. Si no hay remisión en OFIMA, solo se cancelará en el sistema.
              </p>
            )}

            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Nota crédito
              </p>
              <div className="rounded-lg border border-gray-200 p-3">
                <Switch
                  checked={false}
                  onChange={() => {}}
                  disabled
                  label="Nota crédito parcial"
                />
                <p className="mt-1 text-xs text-gray-600">
                  Selecciona los productos para incluir en la nota crédito.
                </p>
              </div>
            </div>

            {ncParcial && (
              <div className="mt-4 bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-800">
                    Productos del pedido
                  </p>
                  <span className="text-xs text-gray-500">
                    {selectedNcSkus.length} seleccionado(s)
                  </span>
                </div>

                {ncProductosLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="inline-block h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                    Cargando productos...
                  </div>
                )}

                {ncProductosError && (
                  <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
                    {ncProductosError}
                  </div>
                )}

                {!ncProductosLoading && !ncProductosError && ncProductoOptions.length === 0 && (
                  <div className="text-sm text-gray-600 bg-white rounded-lg p-3 border border-gray-200">
                    No se encontraron productos para seleccionar.
                  </div>
                )}

                {!ncProductosLoading && !ncProductosError && ncProductoOptions.length > 0 && (
                  <ul className="space-y-2 max-h-56 overflow-auto pr-1" aria-label="Lista de productos para NC parcial">
                    {ncProductoOptions.map((opt) => {
                      const checked = selectedNcSkus.includes(opt.producto)
                      return (
                        <li
                          key={opt.producto}
                          className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${
                            checked ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                          }`}
                        >
                          <label className="flex-1 cursor-pointer select-none" aria-label={`Seleccionar ${opt.producto}`}>
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setSelectedNcSkus(prev => {
                                    if (prev.includes(opt.producto)) {
                                      return prev.filter(s => s !== opt.producto)
                                    }
                                    return [...prev, opt.producto]
                                  })
                                }}
                                disabled={isLoading || anyStepInProgress}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-800 truncate" title={opt.producto}>
                                  {opt.producto}
                                </p>
                                {opt.productName && (
                                  <p className="text-xs text-gray-600 truncate" title={opt.productName}>
                                    {opt.productName}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500">
                                  Cantidad: <span className="font-medium text-gray-700">{opt.cantidad}</span>
                                  {' '}| Valor unitario: <span className="font-medium text-gray-700">{opt.valorUnitario}</span>
                                </p>
                              </div>
                            </div>
                          </label>
                          {checked && <CheckIcon className="h-5 w-5 text-blue-600 flex-shrink-0" aria-hidden />}
                        </li>
                      )
                    })}
                  </ul>
                )}

                <p className="mt-3 text-xs text-gray-500">
                  Si se eligen productos, la NC se creará de forma parcial.
                </p>
              </div>
            )}

            {hasSteps && (
              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                  Progreso
                </p>
                <ul className="relative" aria-label="Pasos de cancelación">
                  {/* Línea vertical conectora */}
                  <li
                    className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-gray-200 rounded-full"
                    aria-hidden
                  />
                  {cancelSteps.map((step, index) => {
                    const isActive = step.status === 'in_progress'
                    const isDone = step.status === 'done'
                    const isError = step.status === 'error'
                    const isSkipped = step.status === 'skipped'
                    const isPending = step.status === 'pending'
                    const isWarningMessage =
                      Boolean(step.message) &&
                      (step.message ?? '').toLowerCase().includes('ya tiene una factura asociada')
                    return (
                      <li
                        key={step.key}
                        className={`relative flex items-start gap-4 pb-4 last:pb-0 transition-all duration-200 ${
                          isActive ? 'opacity-100' : isPending ? 'opacity-70' : 'opacity-100'
                        }`}
                      >
                        <span
                          className={`relative z-10 flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full border-2 transition-all duration-200 ${
                            isDone
                              ? 'border-green-500 bg-green-500 text-white'
                              : isError
                                ? 'border-red-500 bg-red-50 text-red-600'
                                : isSkipped
                                  ? 'border-gray-300 bg-gray-100 text-gray-400'
                                  : isActive
                                    ? 'border-blue-500 bg-blue-50 text-blue-600 ring-2 ring-blue-200'
                                    : 'border-gray-300 bg-white text-gray-400'
                          }`}
                          aria-hidden
                        >
                          {isDone && <CheckCircleIcon className="w-3.5 h-3.5" />}
                          {isError && <XCircleIcon className="w-3.5 h-3.5" />}
                          {isSkipped && <MinusCircleIcon className="w-3.5 h-3.5" />}
                          {isActive && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />}
                          {isPending && <span className="text-[10px] font-semibold">{index + 1}</span>}
                        </span>
                        <div
                          className={`flex-1 min-w-0 pt-0.5 transition-colors duration-200 ${
                            isActive ? 'bg-blue-50/60 rounded-lg px-3 py-2 -mx-1' : ''
                          }`}
                        >
                          <span
                            className={`text-sm font-medium block ${
                              isError ? 'text-red-700' : isSkipped ? 'text-gray-500' : 'text-gray-800'
                            }`}
                          >
                            {step.label}
                            {isSkipped && (
                              <span className="ml-1.5 text-xs font-normal text-gray-400">(omitido)</span>
                            )}
                            {isActive && (
                              <span className="ml-1.5 inline-flex items-center gap-1 text-xs font-normal text-blue-600">
                                <span className="inline-block w-1 h-1 rounded-full bg-blue-500 animate-pulse" />
                                En curso
                              </span>
                            )}
                          </span>
                          {isError && step.message && (
                            <p className="mt-1 text-xs text-red-600 leading-snug">{step.message}</p>
                          )}
                          {!isError && step.message && (
                            <p
                              className={`mt-1 text-xs leading-snug ${
                                isWarningMessage
                                  ? 'text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block'
                                  : 'text-gray-600'
                              }`}
                            >
                              {step.message}
                            </p>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm || isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading || anyStepInProgress ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Cancelando...
                </>
              ) : (
                'Confirmar Cancelación'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CancelOrderModal
